import type { GenderDTO } from "../auth/types";
import { AgentNatureMandate, MandateService } from "../company/types";
import type { DeclarativeIncomeSource, DeclarativeNif } from "../flow/types";
import type { KYCSessionStatus } from "../kyc/types";
import type { DateDTO, DateWithTimezone, NaturalEntityDataType } from "../utils/types";

export type UserType = {
  applications: {
    olkypay: boolean;
    olkywallet: boolean;
  };
  naturalPerson: NaturalPerson;
  olkyPassLevel: KYCSessionStatus;
  identityPartners: identityPartners[];
  identityPartner?: identityPartners;
};
export enum KYPAY_ACCOUNT_TYPE {
  KYPAY_PAYMENT_INSTRUMENT = "Kypay_Payment_Instrument",
  KYPAY_PAYMENT_SOLUTION = "Kypay_Payment_Solution"
}

// TODO : Most of these variables are nullable, see master-sf
export type identityPartners = {
  uuid: string;
  name: string;
  force_webauthn: boolean | null;
  allow_crypto: boolean | null;
  logo: string;
  website: string;
  host: string;
  mode: string;
  minimum_olkypass_level: number;
  minimum_olkypass_pro_level: number;

  token: {
    address: string;
    name: string;
    symbol: string;
    native_symbol: string | null;
    decimal: string;
  };
  trigger_type: string;
  participation_opening_date: string;
  participation_closing_date: string;
  kypay_account_type: KYPAY_ACCOUNT_TYPE;
  custom_scenario: string | null;
};
export type CachedUser = {
  email?: string;
  name?: string;
  avatar?: string;
};

export type proFlowNP = {
  naturalPerson: NaturalPerson;
};
export type NaturalPerson = {
  Gender: string;
  LastName: string;
  FirstName: string;
  CommonFirstName: string | null;
  CommonName: string | null;
  OtherFirstNames: null | string;
  MaidenName: null | string;
  DateOfBirth: DateWithTimezone;
  CountryOfCitizenship: number;
  CountryOfCitizenship2: number | null;
  CountryOfCitizenship3: number | null;
  CountryOfCitizenship4: number | null;
  Birthplace: null | string;
  StateOfBirth: string;
  Address: AddressFullType[]; // Replace any with a more specific type if possible
  AliasIBAN: AliasIBAN[]; // Replace any with a more specific type if possible
  AliasMobile: AliasMobile[]; // Replace any with a more specific type if possible
  AliasEmail: AliasEmail[];
  NIF: null | string;
  nif_country: null | string;
  Comments: null | string;
  CodeLangue: null | string;
  ActivityCode: number;
  CSPCode: number;
  description?: string | null;
  codeShop: null | string;
  idBB: null | string;
  ibanOlkyPay: null | string;
  countryOfBirth: number;
  uuid: string;
  UUID: string;
  TaxAddress?: DeclarativeNif[];
  olkyAccount?: OlkyAccount | null;
  minimumMonthlyIncome: number | null;
  maximumMonthlyIncome: number | null;
  sourcesIncome: DeclarativeIncomeSource[];
  olkypay_account_requested?: number;
  activityDescription?: string | null;
  identityUuid?: string | null;
  mail: string;
};

export type NaturalPersonMissingFields = {
  gender: string;
  lastName: string;
  firstName: string;
  commonFirstName: string | null;
  commonName: string | null;
  otherFirstNames: null | string;
  dateOfBirth: DateWithTimezone;
  countryOfCitizenship: number;
  countryOfCitizenship2: number | null;
  countryOfCitizenship3: number | null;
  countryOfCitizenship4: number | null;
  stateOfBirth: string;
  address: AddressFullType[];
  aliasMobile: AliasMobile[];
  codeLangue: string;
  activityCode: number;
  cspCode: number;
  description?: string | null;
  countryOfBirth: number;
  taxAddress?: DeclarativeNif[];
  minimumMonthlyIncome: number | null;
  maximumMonthlyIncome: number | null;
  sourcesIncome: DeclarativeIncomeSource[];
  activityDescription?: string | null;
};

export type OlkyAccount = {
  iban: string;
  balanceOperationYesterday: string;
  balanceOperationDate: string;
  supplierId: {
    logoPath: string | null;
  };
  codeShop: string;
  balance: string;
};

export type AliasIBAN = {
  UUID: string;
  Owner: string;
  Iban: string;
  BIC: string;
  Actif: boolean;
  Public: boolean;
  Main: boolean;
  BankInfoDto: {
    Address: string;
    CountryName: string;
    Institution: string;
    Location: string;
    Name: string;
  };
  valid: boolean;
};

// Different DTO schemas sent by backend are named "NaturalPerson", we name them differently to differenciate them
export type FullNaturalPerson = {
  HasToSendToLausanne: boolean;
  HasToSendToAlgoreg: boolean;
  id: number;
  uuid: string;
  gender: GenderDTO;
  last_name: string | null;
  first_name: string | null;
  other_first_names: string | null;
  maiden_name: string | null;
  birth_date: DateDTO | string;
  birth_city: string | null;
  birth_state: string | null;
  pep: string | null;
  id_bb: string | null;
  created_at: string;
  updated_at: string;
  deleted_date: string | null;
  country_of_citizenship: FullCountry;
};

export type RealIdentity = {
  id: number;
  uuid: string;
  risk: string | null;
  nif: string;
  nif_country: string;
  internal_id: number | null;
  comment: string | null;
  status: string | null;
  language_code: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_date: string | null;
  algoreg_upload: boolean;
  lausanne_upload: boolean;
  code_shop: string | null;
  data_validated_by_user: boolean;
  lausanne_id: string | null;
  algoreg_external_id: string | null;
  natural_person: FullNaturalPerson;
  kycoin_wallet: "[Circular]";
  alias_ibans: FullAliasIBAN[];
  email: FullAliasEmail;
  home_address: FullAddress;
};

export type AliasEmail = {
  UUID: string;
  Owner: string;
  Mail: string;
  Actif: boolean;
  Public: boolean;
  LinkNPLE: null | string;
  valid: boolean;
};

export type AliasMobile = {
  UUID: string;
  Owner: string;
  Phone: string;
  Public: boolean;
  MobileIndicatif: string;
  MobileNumber: string;
  Main: boolean;
};

export type MobileCreate = {
  UUID?: string;
  MobileNumber: string;
  MobileIndicatif: string;
  main: boolean;
};

export type IbanCreate = {
  UUID: string;
  iban: string;
  main?: boolean;
};

export type FullAliasEmail = {
  id: number;
  uuid: string | null;
  mail: string;
  active: boolean;
  public: boolean;
  valid: boolean;
  created_at: string;
  updated_at: string;
  deleted_date: string | null;
};

export type FullAliasIBAN = {
  id: number;
  uuid: string;
  iban: string;
  active: boolean;
  public: boolean;
  main: boolean;
  bic: string;
  created_at: string;
  updated_at: string;
  deleted_date: string | null;
  mirror_identity: string | null;
};

export type AddressFullType = {
  Address1: string;
  ZipCode: string;
  Country: string;
  City: string;
  Tag?: string;
  UUID?: string;
  language?: string;
  isPrincipalAddress?: boolean;
};

export type AdressWithCountryUuid = AddressFullType & {
  CountryUuid: string;
};

export const isAddressCreateEqualToAddress = (
  addressCreate: AddressCreate,
  addressParam: AddressFullType
) =>
  addressCreate.address === addressParam?.Address1 &&
  addressCreate.country === parseInt(addressParam?.Country) &&
  addressCreate.zip === addressParam?.ZipCode &&
  addressCreate.city === addressParam?.City &&
  addressCreate.tag === addressParam?.Tag &&
  addressCreate.isPrincipalAddress === addressParam.isPrincipalAddress;

export type AddressType = {
  address_1: string;
  zip_code: string;
  city: string;
  country_id: number | null;
};

export type FullAddress = {
  id: number;
  uuid: string;
  address_1: string;
  address_2: string | null;
  address_3: string | null;
  zip_code: string;
  city: string;
  tag: string;
  created_at: string;
  updated_at: string;
  deleted_date: string | null;
  country: FullCountry;
};

export type AddressCreate = {
  tag: string;
  address: string;
  city: string;
  country: number | null;
  zip: string;
  UUID?: string;
  language?: string;
  isPrincipalAddress?: boolean;
};

export type FullCountry = {
  id: number;
  uuid: string;
  name: string;
  defaultName: string;
  Code: string;
  alpha2: string;
  alpha3: string;
  ibanAllowSize: number | null;
  englishName: string;
  countryValidForIban: boolean;
  openOlkypayAccount: boolean;
};

export type Phone = {
  MobileIndicatif: string;
  MobileNumber: string;
  UUID: string;
  main: boolean;
};

export type Iban = {
  iban: string;
  requestValidate?: boolean;
  main: boolean;
};

export type NaturalPersonDTO = Omit<
  Omit<
    Omit<
      Omit<Omit<NaturalPerson, "CountryOfCitizenship">, "CountryOfCitizenship2">,
      "CountryOfCitizenship3"
    >,
    "CountryOfCitizenship4"
  >,
  "countryOfBirth"
> & {
  CountryOfCitizenship: string;
  CountryOfCitizenship2: string | null;
  CountryOfCitizenship3: string | null;
  CountryOfCitizenship4: string | null;

  countryOfBirth: string;
  CodeLanguage?: string;
};

export const mapNpToNpDto = (np: NaturalPerson): NaturalPersonDTO => {
  return {
    ...np,
    CountryOfCitizenship: np.CountryOfCitizenship?.toString(),
    CountryOfCitizenship2: np.CountryOfCitizenship2?.toString() ?? null,
    CountryOfCitizenship3: np.CountryOfCitizenship3?.toString() ?? null,
    CountryOfCitizenship4: np.CountryOfCitizenship4?.toString() ?? null,
    countryOfBirth: np.countryOfBirth?.toString() ?? ""
  };
};

export type StaticActivity = {
  Code: number;
  defaultName: string;
  Nom: string;
  NomEn: string;
  Risk: number;
  id: number;
  uuid: string;
};

export type StaticCSP = {
  Code: number;
  defaultName: string;
  Nom: string;
  NomEn: string;
  Risk: number;
  id: number;
  uuid: string;
};
export type StaticNatureMandate = {
  default_name: AgentNatureMandate;
  name: string;
  name_fr: string;
  id: number;
  uuid: string;
};
export type StaticMandateService = {
  default_name: MandateService;
  name: string;
  name_fr: string;
  id: number;
  uuid: string;
};
export type StaticCountry = {
  Code: string;
  alpha2: string;
  alpha3: string;
  countryValidForIban: boolean | null;
  englishName: string;
  defaultName: string;
  ibanAllowSize: number;
  id: number;
  name: string;
  uuid: string;
};

export type StaticNace = {
  englishName: string;
  defaultName: string;
  id: string;
  name: string;
};
export type legal_form_type = {
  id: number;
  uuid: string;
  name: string;
  code: string;
};

export type StaticLegalStatus = {
  englishName: string;
  defaultName: string;
  id: string;
  name: string;
};

export type StaticData = {
  activities: StaticActivity[];
  cspCodes: StaticCSP[];
  countries: StaticCountry[];
  naceCodes: StaticNace[];
  legalStatus: StaticLegalStatus[];
};

export type CredentialType = {
  username: string;
  password: string;
};
export type CityInfoType = {
  nom: string;
  code: string;
  departement: {
    code: string;
    nom: string;
  };
  codesPostaux: string[];
  codesPostal?: string;
};

export enum PublicAliasType {
  Email = "email",
  Iban = "iban",
  Phone = "phone"
}

export type AliasDelete = {
  UUID: string;
  ItemUUID: string;
  ItemType:
    | NaturalEntityDataType.AliasMailDto
    | NaturalEntityDataType.AliasIBANDto
    | NaturalEntityDataType.AliasMobileDto
    | NaturalEntityDataType.AdresseDto;
};

export type EmailCodeResponse = {
  cypher: string;
};
export type nifValidationResponse = {
  countryCode: string;
  tinNumber: string;
  requestDate: string;
  validStructure: boolean;
  validSyntax: boolean;
};
export type NewEmailMutationProps = {
  email: string;
};

export type ValidateNifMutationProps = {
  countryCode: string;
  nif: string;
};
export type MandateServiceType = MandateService.KYPAY_WITHDRAW | MandateService.ALIASES_MANAGEMENT;
export enum ErrorWalletCodeEnum {
  HOLDER_ID_NOT_FOUNT = 24006
}
